import { FAIcon } from '@/components/fa-icon'
import { TableBody, TableCell, TableRow } from '@/components/ui'
import { Tooltip } from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, isSameDay, parseISO } from 'date-fns'
import { useTheme } from 'styled-components'
import type { ApprovalRestPeriodNode } from '../../types.graphql'
import { DietButton } from '../DietButton'
import { SalaryDayButton } from '../SalaryDayButton'
import {
  formatRestDuration,
  restNights,
  restTypeLabel,
  useRestLabels,
} from './rest-period-format'
import { RestPaidSwitch } from './rest-paid-switch'

/**
 * A daily rest, drawn as the seam between the two shifts rather than as a row.
 * There are as many of them as there are shifts, so giving each a full row
 * would double the list for nothing. When its salary work time pays daily
 * rests, the band carries the switch that leaves this one rest unpaid.
 */
export const RestBandRow: React.FC<{
  restPeriod: ApprovalRestPeriodNode
  columnCount: number
  canChangePaid: boolean
  onChange: () => void
}> = ({ restPeriod, columnCount, canChangePaid, onChange }) => {
  const labels = useRestLabels()
  const theme = useTheme()
  const start = parseISO(restPeriod.datetimeStart)
  const end = parseISO(restPeriod.datetimeEnd)

  return (
    <TableBody className="border-t border-gray-200">
      <TableRow>
        <TableCell colSpan={columnCount} className="bg-stone-100 py-1.5">
          <span className="flex items-center gap-2 font-mono text-xs text-stone-600">
            <FAIcon
              icon="moon"
              type="solid"
              style={{ color: theme.colors.primary500 }}
            />
            <span>
              {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
            </span>
            <span>{formatRestDuration(start, end)}</span>
            <span>
              {restTypeLabel(restPeriod.restType, labels).toLowerCase()}
            </span>
            <RestPaidSwitch
              restPeriod={restPeriod}
              canChangePaid={canChangePaid}
              onChange={onChange}
              className="ml-auto"
            />
          </span>
        </TableCell>
      </TableRow>
    </TableBody>
  )
}

const NotApplicable = () => <span className="text-stone-400">–</span>

/**
 * A rest has no approval of its own: it is approved, locked and paid with the
 * shift it follows, which is also what decides its department and its salary
 * work time. Showing the inherited state read-only is honest about that — a
 * blank cell reads as "nobody has looked at this yet".
 */
const InheritedApproval: React.FC<{ approved: boolean; label: string }> = ({
  approved,
  label,
}) => (
  <Tooltip content={label}>
    <span className="inline-flex items-center text-stone-400">
      <FAIcon icon={approved ? 'circle-check' : 'circle-minus'} />
    </span>
  </Tooltip>
)

interface RestPeriodRowProps {
  restPeriod: ApprovalRestPeriodNode
  userId: string
  canEdit: boolean
  showWorkHours: boolean
  showPause: boolean
  showSalaryMode: boolean
  showEmployeeGroup: boolean
  showDepartment: boolean
  showFacility: boolean
  showDiet: boolean
  showAddons: boolean
  showApprovedByDriver: boolean
  showApprovedByManager: boolean
  showTransferredToFlex: boolean
  onChange: () => void
}

/**
 * A weekly (or reduced weekly) rest, drawn as a full row because it is the one
 * that carries money. Its cells line up with a shift row's: the salary day sits
 * under the salary work time it inherits from the shift before it, the diet in
 * the diet column. The rest is read-only data, so the inherited salary work
 * time is shown as text — there is nothing on a rest to change it to.
 */
export const RestPeriodRow: React.FC<RestPeriodRowProps> = ({
  restPeriod,
  userId,
  canEdit,
  showWorkHours,
  showPause,
  showSalaryMode,
  showEmployeeGroup,
  showDepartment,
  showFacility,
  showDiet,
  showAddons,
  showApprovedByDriver,
  showApprovedByManager,
  showTransferredToFlex,
  onChange,
}) => {
  const labels = useRestLabels()
  const theme = useTheme()
  const translations = useTranslate({
    approvalFollowsShift: 'salary.rest-approval-follows-shift',
  })
  const start = parseISO(restPeriod.datetimeStart)
  const end = parseISO(restPeriod.datetimeEnd)
  const dateLine = isSameDay(start, end)
    ? format(start, 'EEE dd. MMMM')
    : `${format(start, 'EEE dd. MMM')} – ${format(end, 'EEE dd. MMM')}`

  return (
    <TableBody className="border-t border-gray-200 [&>tr>td]:bg-stone-100/60">
      <TableRow>
        <TableCell>
          {/* The row keeps the neutral tint -- the approval colours mean
              approval state, and a rest has none. Blue marks it instead. */}
          <FAIcon
            icon="moon"
            type="solid"
            style={{ color: theme.colors.primary500 }}
          />
        </TableCell>
        <TableCell>
          <div className="flex min-w-0 flex-col gap-1">
            <span className="font-medium text-stone-700">
              {restTypeLabel(restPeriod.restType, labels)}
            </span>
            <span className="text-sm text-stone-500">{dateLine}</span>
          </div>
        </TableCell>
        <TableCell narrow>
          <div className="text-2xl leading-none font-semibold text-stone-600">
            {format(start, 'HH:mm')}
          </div>
        </TableCell>
        <TableCell narrow>
          <div className="flex flex-col gap-1">
            <span className="text-2xl leading-none font-semibold text-stone-600">
              {format(end, 'HH:mm')}
            </span>
            <span className="font-mono text-sm text-stone-500">
              {formatRestDuration(start, end)}
            </span>
          </div>
        </TableCell>
        {showWorkHours && (
          <TableCell>
            <NotApplicable />
          </TableCell>
        )}
        {showPause && (
          <TableCell>
            <NotApplicable />
          </TableCell>
        )}
        {showSalaryMode && (
          <TableCell narrow>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-stone-500 italic">
                {restPeriod.salarySetting?.name ?? '–'}
              </span>
              {restPeriod.salarySetting?.salaryType === 'DAY' && (
                <SalaryDayButton
                  restPeriod={{ ...restPeriod, nights: restNights(start, end) }}
                  salaryDay={restPeriod.salaryDay}
                  userId={userId}
                  canEdit={canEdit}
                  border
                  onChange={onChange}
                  restrictWidth
                />
              )}
            </div>
          </TableCell>
        )}
        {showEmployeeGroup && (
          <TableCell>
            <NotApplicable />
          </TableCell>
        )}
        {showDepartment && (
          <TableCell>
            <NotApplicable />
          </TableCell>
        )}
        {showFacility && (
          <TableCell>
            <NotApplicable />
          </TableCell>
        )}
        {showDiet && (
          <TableCell narrow>
            <DietButton
              restPeriod={{ ...restPeriod, nights: restNights(start, end) }}
              userId={userId}
              canEdit={canEdit}
              border
              onChange={onChange}
            />
          </TableCell>
        )}
        {showAddons && (
          <TableCell>
            <NotApplicable />
          </TableCell>
        )}
        {showApprovedByDriver && (
          <TableCell>
            <InheritedApproval
              approved={restPeriod.workPeriodBefore.approvedByUser}
              label={translations.approvalFollowsShift}
            />
          </TableCell>
        )}
        {showApprovedByManager && (
          <TableCell>
            <InheritedApproval
              approved={restPeriod.workPeriodBefore.approvedByManager}
              label={translations.approvalFollowsShift}
            />
          </TableCell>
        )}
        {showTransferredToFlex && (
          <TableCell>
            <NotApplicable />
          </TableCell>
        )}
      </TableRow>
    </TableBody>
  )
}
